/**
 * Money helpers.
 *
 * All amounts are stored as decimal numbers in the major unit (Pula, Rand,
 * Naira) for compactness. Formatters wrap Intl.NumberFormat keyed on the
 * region's locale so the same code renders "P 1,234.00" in Botswana,
 * "R 1 234,00" in South Africa, and "₦1,234.00" in Nigeria.
 *
 * The legacy Pula-only helpers (toThebe, formatPula, formatPulaCompact)
 * are preserved as thin wrappers around the new region-aware functions —
 * they default to Botswana so existing call-sites and tests don't change.
 */

import { getRegion, type RegionCode } from './region';

export const THEBE_PER_PULA = 100;

/** Convert a major-unit decimal (e.g. Pula) to integer minor units. Rounds half-up. */
export function toMinorUnits(amount: number, minorPer: number = THEBE_PER_PULA): number {
  return Math.round(amount * minorPer);
}

/** Convert integer minor units back to the major decimal unit. */
export function toMajorUnits(minor: number, minorPer: number = THEBE_PER_PULA): number {
  return minor / minorPer;
}

/** Convert Pula → thebe (legacy alias for the Botswana case). */
export function toThebe(pula: number): number {
  return toMinorUnits(pula, THEBE_PER_PULA);
}

/** Convert thebe → Pula (legacy alias for the Botswana case). */
export function toPula(thebe: number): number {
  return toMajorUnits(thebe, THEBE_PER_PULA);
}

/**
 * Region-aware money formatter — "P 15,300.00", "R 15 300,00", "₦15,300.00".
 */
export function formatMoney(
  amount: number,
  region: RegionCode | string = 'BW',
  opts: { withSymbol?: boolean } = {},
): string {
  const cfg = getRegion(region);
  const withSymbol = opts.withSymbol ?? true;
  const formatter = new Intl.NumberFormat(cfg.locale, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const formatted = formatter.format(amount);
  return withSymbol ? `${cfg.currencySymbol} ${formatted}` : formatted;
}

/** Compact form for KPI cards: "P 15.3k", "R 1.2M", "₦15.3k". */
export function formatMoneyCompact(amount: number, region: RegionCode | string = 'BW'): string {
  const cfg = getRegion(region);
  const sym = cfg.currencySymbol;
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${sym} ${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${sym} ${(abs / 1_000).toFixed(1)}k`;
  return formatMoney(amount, region);
}

/** Legacy Botswana-only formatter. Kept for back-compat with existing call sites. */
export function formatPula(pula: number, opts: { withSymbol?: boolean } = {}): string {
  return formatMoney(pula, 'BW', opts);
}

/** Legacy Botswana-only compact formatter. */
export function formatPulaCompact(pula: number): string {
  return formatMoneyCompact(pula, 'BW');
}
